import { useCallback, useEffect, useState } from "react";
import { Columns3, FileSpreadsheet, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminApi, downloadFile, formatApiError } from "@/lib/adminApi";
import { VendorList } from "@/components/admin/VendorList";
import { VendorDetail } from "@/components/admin/VendorDetail";
import { VendorCompare } from "@/components/admin/VendorCompare";

export const VendorPricingTab = () => {
  const [subs, setSubs] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [view, setView] = useState({ type: "list" });
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/vendor-submissions");
      setSubs(data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error("Could not load submissions");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (s.size === subs.length ? new Set() : new Set(subs.map((x) => x.submission_id))));

  const onPdf = (s) => downloadFile(`/admin/vendor-submissions/${s.submission_id}/pdf`, `hausA-vendor-pricing-${s.submission_id.slice(0, 8)}.pdf`)
    .catch(() => toast.error("PDF download failed"));
  const onCsv = () => downloadFile("/admin/vendor-submissions/export.csv", "hausA-vendor-pricing.csv").catch(() => toast.error("CSV export failed"));
  const onResend = async (s) => {
    setBusyId(s.submission_id);
    try {
      await adminApi.post(`/admin/vendor-submissions/${s.submission_id}/resend`);
      toast.success("Email queued. Status updates in a few seconds.");
      setSubs((list) => list.map((x) => (x.submission_id === s.submission_id ? { ...x, email_status: "pending", email_error: null } : x)));
      setTimeout(load, 6000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Resend failed"));
    } finally {
      setBusyId(null);
    }
  };

  if (!subs) return <p className="hx-empty">Loading submissions…</p>;
  const actions = { onPdf, onResend, busy: false };

  if (view.type === "detail") {
    const sub = subs.find((s) => s.submission_id === view.id);
    if (sub) return <VendorDetail sub={sub} onBack={() => setView({ type: "list" })} {...actions} busy={busyId === sub.submission_id} />;
  }
  if (view.type === "compare") {
    const chosen = subs.filter((s) => view.ids.includes(s.submission_id));
    if (chosen.length) return <VendorCompare subs={chosen} onBack={() => setView({ type: "list" })} />;
  }

  return (
    <div data-testid="vendor-pricing-tab">
      <div className="hx-toolbar">
        <span className="hx-count" data-testid="vendor-sub-count">{subs.length} submission{subs.length === 1 ? "" : "s"}</span>
        <div className="hx-toolbar__group">
          <button className="hx-btn hx-btn--ghost hx-btn--sm" onClick={load} data-testid="vendor-refresh-button"><RefreshCw size={14} /> Refresh</button>
          <button className="hx-btn hx-btn--ghost hx-btn--sm" disabled={selected.size < 1} onClick={() => setView({ type: "compare", ids: [...selected] })} data-testid="vendor-compare-selected-button"><Columns3 size={14} /> Compare selected ({selected.size})</button>
          <button className="hx-btn hx-btn--ghost hx-btn--sm" disabled={!subs.length} onClick={() => setView({ type: "compare", ids: subs.map((s) => s.submission_id) })} data-testid="vendor-compare-all-button"><Columns3 size={14} /> Compare all</button>
          <button className="hx-btn hx-btn--sm" disabled={!subs.length} onClick={onCsv} data-testid="vendor-export-csv-button"><FileSpreadsheet size={14} /> Export CSV</button>
        </div>
      </div>
      {subs.length === 0 ? (
        <p className="hx-empty" data-testid="vendor-empty-state">No vendor submissions yet. Share the public form link above.</p>
      ) : (
        <VendorList subs={subs} selected={selected} onToggle={toggle} onToggleAll={toggleAll} busyId={busyId} onView={(id) => setView({ type: "detail", id })} onPdf={onPdf} onResend={onResend} />
      )}
    </div>
  );
};
